import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';

import { SpinnerButtonComponent } from '../spinner-button/spinner-button.component';

/**
 * Reusable legal-consent form: a transparency summary + three mandatory
 * checkboxes (Terms, Privacy + processing consent, data-controller
 * responsibility). The submit button stays disabled until all three are
 * ticked. The parent owns the async call — it passes `busy` and reacts to
 * the `accepted` output.
 *
 * Used both as the onboarding consent step body and inside the blocking
 * re-consent dialog.
 */
@Component({
  selector: 'app-legal-consent-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    SpinnerButtonComponent,
  ],
  template: `
    <form [formGroup]="form" (ngSubmit)="submit()" class="legal-consent-form">
      <!-- Transparency-by-default summary -->
      <aside class="transparency">
        <h3>
          <mat-icon>shield</mat-icon>
          {{ 'legal.consent.transparencyTitle' | translate }}
        </h3>
        <ul>
          <li>
            <mat-icon>inventory_2</mat-icon>
            <span>{{ 'legal.consent.transparencyRecord' | translate }}</span>
          </li>
          <li>
            <mat-icon>settings_suggest</mat-icon>
            <span>{{ 'legal.consent.transparencyUse' | translate }}</span>
          </li>
          <li>
            <mat-icon>share</mat-icon>
            <span>{{ 'legal.consent.transparencyShare' | translate }}</span>
          </li>
        </ul>
        <a
          class="full-policy"
          [routerLink]="['/legal/privacy']"
          target="_blank"
          rel="noopener"
        >
          {{ 'legal.consent.readFullPolicy' | translate }}
          <mat-icon>open_in_new</mat-icon>
        </a>
      </aside>

      <!-- Mandatory consents -->
      <div class="consents">
        <mat-checkbox formControlName="terms" class="consent-row">
          <span>{{ 'legal.consent.acceptTermsPrefix' | translate }}</span>
          <a
            [routerLink]="['/legal/terms']"
            target="_blank"
            rel="noopener"
            (click)="$event.stopPropagation()"
          >{{ 'legal.consent.termsLink' | translate }}</a>
          <span>{{ 'legal.consent.acceptTermsSuffix' | translate }}</span>
        </mat-checkbox>

        <mat-checkbox formControlName="privacy" class="consent-row">
          <span>{{ 'legal.consent.acceptPrivacyPrefix' | translate }}</span>
          <a
            [routerLink]="['/legal/privacy']"
            target="_blank"
            rel="noopener"
            (click)="$event.stopPropagation()"
          >{{ 'legal.consent.privacyLink' | translate }}</a>
          <span>{{ 'legal.consent.acceptPrivacySuffix' | translate }}</span>
        </mat-checkbox>

        <mat-checkbox formControlName="responsibility" class="consent-row">
          {{ 'legal.consent.acceptResponsibility' | translate }}
        </mat-checkbox>
      </div>

      @if (showErrors() && form.invalid) {
        <p class="required-hint">
          <mat-icon>error_outline</mat-icon>
          {{ 'legal.consent.requiredHint' | translate }}
        </p>
      }

      <footer class="form-footer">
        <app-spinner-button
          type="submit"
          class="cta"
          [loading]="busy()"
          [disabled]="form.invalid"
        >
          {{ submitLabelKey() | translate }}
          <mat-icon>arrow_forward</mat-icon>
        </app-spinner-button>
      </footer>
    </form>
  `,
  styles: [`
    .legal-consent-form { display: grid; gap: 22px; }

    .transparency {
      border-radius: 12px;
      border: 1px solid var(--border, rgba(0, 0, 0, 0.10));
      background: var(--background-card, #ffffff);
      padding: 16px 18px;

      h3 {
        display: flex; align-items: center; gap: 8px;
        font-size: 15px; font-weight: 600; margin: 0 0 12px;
        mat-icon { color: var(--color-primary, #3b82f6); }
      }
      ul { list-style: none; padding: 0; margin: 0; display: grid; gap: 10px; }
      li {
        display: flex; align-items: flex-start; gap: 10px;
        font-size: 13.5px; line-height: 1.45; color: var(--text-secondary, #4b5563);
        mat-icon {
          font-size: 19px; width: 19px; height: 19px; margin-top: 1px;
          color: var(--color-primary, #3b82f6); flex-shrink: 0;
        }
      }
      .full-policy {
        display: inline-flex; align-items: center; gap: 4px;
        margin-top: 12px; font-size: 13px; font-weight: 600;
        color: var(--color-primary, #3b82f6); text-decoration: none;
        mat-icon { font-size: 15px; width: 15px; height: 15px; }
        &:hover { text-decoration: underline; }
      }
    }

    .consents { display: grid; gap: 14px; }

    .consent-row {
      font-size: 14px; line-height: 1.45;
      a {
        /* Angular collapses the whitespace between the prefix <span> and this
           inline link, gluing "I accept the" straight onto "terms of use".
           A start-margin restores the gap in every locale without touching the
           suffix (a bare "." that must stay attached to the link). */
        margin-inline-start: 0.28em;
        color: var(--color-primary, #3b82f6); font-weight: 600;
        text-decoration: none;
        &:hover { text-decoration: underline; }
      }
    }

    .required-hint {
      display: flex; align-items: center; gap: 6px; margin: 0;
      color: var(--mdc-theme-error, #d32f2f); font-size: 13px;
      mat-icon { font-size: 18px; width: 18px; height: 18px; }
    }

    .form-footer { display: flex; justify-content: center; }
    .cta { font-size: 15px; font-weight: 600; }
  `],
})
export class LegalConsentFormComponent {
  /** Parent-controlled in-flight state (spinner + disabled submit). */
  readonly busy = input(false);
  /** i18n key for the submit button label. */
  readonly submitLabelKey = input('legal.consent.submit');

  /** Emitted once all three boxes are ticked and submit is pressed. */
  readonly accepted = output<void>();

  readonly showErrors = signal(false);

  readonly form = inject(FormBuilder).nonNullable.group({
    terms: [false, Validators.requiredTrue],
    privacy: [false, Validators.requiredTrue],
    responsibility: [false, Validators.requiredTrue],
  });

  submit(): void {
    if (this.busy()) return;
    if (this.form.invalid) {
      this.showErrors.set(true);
      this.form.markAllAsTouched();
      return;
    }
    this.accepted.emit();
  }
}
